import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { FieldComponentType } from "@smart/bridge-intake-components-dom";
import {
  Badge,
  Card,
  DragHandleProps,
  Icon,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";

import { Actions } from "./actions";
import { FieldTypeIndicator } from "./field-type-indicator";
import { LanguageKey, baseLanguageKey } from "../../../../types";
import { fieldComponentNames } from "../../constants";
import { ConditionsButton } from "../../edit-view/condition/conditions-button";
import { EditableText } from "../../editor";
import {
  ConditionActions,
  FieldTemplate,
  GqlFieldValues,
  GqlGroupValues,
  GqlMatterLayout,
  GqlMatterType,
  LoadMatterFields,
  OnDeleteField,
  OnFieldTypeChange,
  OnUpdateField,
} from "../../types";
import {
  ChangeStatus,
  changeStatusLabel,
  isChanged,
  isWebEnquiryClientNameField,
} from "../../utils";
import { FieldBox } from "../field-box";

type FieldCardProps = {
  loading?: boolean;
  dragHandleProps?: DragHandleProps | null;
  isShrinking?: boolean;
  isDragging?: boolean;
  editing?: boolean;
  onUpdateField: OnUpdateField;
  onDeleteField: OnDeleteField;
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  loadMatterFields: LoadMatterFields;
  field: GqlFieldValues;
  conditionActions: ConditionActions;
  groupLayout?: GqlGroupValues["layout"] | null;
  groupMatterField?: GqlGroupValues["field"] | null;
  onFieldTypeChange: OnFieldTypeChange;
  excludingFieldTemplates: FieldTemplate[];
  className?: string;
  lastActiveIds: string[];
  viewingLanguage: LanguageKey;
  hasSubmissionLinks?: boolean;
  changeStatus?: ChangeStatus;
  allowChangeFieldType?: boolean;
};

export const FieldCard = ({
  loading,
  isShrinking,
  isDragging,
  onUpdateField,
  onDeleteField,
  dragHandleProps,
  editing,
  matterLayouts,
  matterTypes,
  loadMatterFields,
  field,
  conditionActions,
  groupLayout,
  groupMatterField,
  onFieldTypeChange,
  excludingFieldTemplates,
  className,
  lastActiveIds,
  viewingLanguage,
  hasSubmissionLinks,
  changeStatus,
  allowChangeFieldType = true,
}: FieldCardProps) => {
  const {
    type,
    label: question,
    description,
    mandatory,
    includeInSummary,
    field: matterField,
    layout,
    translations,
  } = field;

  const fieldTranslation =
    viewingLanguage !== baseLanguageKey
      ? translations?.find(
          (translation) => translation.language === viewingLanguage,
        )
      : undefined;
  const translatedQuestion = fieldTranslation?.label;
  const translatedDescription = fieldTranslation?.description;
  const hasDescription = Boolean(translatedDescription ?? description);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [editedQuestion, setEditedQuestion] = useState(
    translatedQuestion || question || fieldComponentNames[type],
  );
  const [editedDescription, setEditedDescription] = useState(
    translatedDescription || description,
  );
  const [descriptionVisible, setDescriptionVisible] = useState(hasDescription);
  const [isEditing, setIsEditing] = useState(editing);

  const handleEditComplete = () =>
    onUpdateField({
      field,
      updated: {
        label: editedQuestion,
        description: editedDescription ?? undefined,
      },
    });

  const handleSaveLabel = async (value: string) => {
    setEditedQuestion(value);
    await onUpdateField({
      field,
      updated: { label: value },
    });
  };

  const handleSaveDescription = async (value: string) => {
    setEditedDescription(value);
    await onUpdateField({
      field,
      updated: { description: value },
    });
  };

  useEffect(() => {
    setIsEditing(editing);
  }, [editing]);

  useEffect(() => {
    setEditedQuestion(
      translatedQuestion || question || fieldComponentNames[type],
    );
    setEditedDescription(translatedDescription || description);
    setDescriptionVisible(Boolean(translatedDescription ?? description));
  }, [translatedQuestion, translatedDescription, question, description, type]);

  const onChangeFieldType = async (newFieldType: FieldComponentType) => {
    await onFieldTypeChange({ field, newFieldType });
  };

  const renderEditTo = () => wrapperRef.current;

  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;

  return (
    <div
      ref={wrapperRef}
      className={clsx(
        "flex flex-col justify-center grow w-full content-center flex-wrap",
        className,
      )}
    >
      <Card
        className={clsx(
          "border-solid border-2 p-[0.4rem] pr-[2.8rem] shadow-none transition-colors duration-300",
          (isDragging || lastActiveIds.includes(field.uri)) &&
            "border-cyan-240",
        )}
        flow="row"
        loading={loading}
      >
        {/* The card border is already used to indicate dragging state.
         Using a separate div to indicate added/edited fields. */}
        {isChanged(changeStatus) && (
          <div
            data-testid="change-indicator"
            className="absolute -left-[0.2rem] -top-[0.2rem] -bottom-[0.2rem] w-[0.4rem] bg-blue-460 rounded-l"
          />
        )}
        <div className="flex gap-[0.4rem] w-full">
          <div
            className={clsx(
              "flex items-center justify-center px-[1.05rem] rounded-l hover:bg-black/5",
              isDragging && "cursor-grab",
              !isViewingBaseLanguage &&
                "cursor-not-allowed opacity-50 hover:bg-transparent",
            )}
            {...(isViewingBaseLanguage
              ? {
                  ...dragHandleProps?.attributes,
                  ...dragHandleProps?.listeners,
                }
              : {})}
          >
            <Icon
              name="solidGripDotsVertical"
              className="w-[1.1rem] h-[2.2rem]"
            />
          </div>
          <div className="flex flex-col justify-center w-full overflow-hidden pb-[1.2rem]">
            <div className="flex items-center w-full mt-[0.8rem] mb-[0.8rem] pr-[0.4rem]">
              <div className="flex flex-col items-start justify-center relative flex-1 overflow-hidden">
                <div className="flex items-start gap-small w-full mt-[0.4rem]">
                  <EditableText
                    text={editedQuestion}
                    onChange={setEditedQuestion}
                    onComplete={handleEditComplete}
                    onSave={handleSaveLabel}
                    isMandatory={mandatory}
                    editing={isEditing}
                    onEditing={setIsEditing}
                    fontWeight={type === "info" ? "normal" : "semibold"}
                    isEditor
                    renderEditTo={renderEditTo}
                  />
                  {isChanged(changeStatus) && (
                    <Badge
                      className="mt-[0.4rem]"
                      label={changeStatusLabel[changeStatus]}
                      theme="information"
                    />
                  )}
                </div>
                {descriptionVisible && type !== "info" && (
                  <EditableText
                    text={editedDescription}
                    onChange={setEditedDescription}
                    onComplete={handleEditComplete}
                    onSave={handleSaveDescription}
                    placeholder="Add description text to guide clients on what to enter. This won't show on the final form submission."
                    isEditor
                    renderEditTo={renderEditTo}
                  />
                )}
              </div>
              <Actions
                type={type}
                descriptionVisible={descriptionVisible}
                mandatory={mandatory}
                includeInSummary={!!includeInSummary}
                onToggleDescription={async (checked) => {
                  setDescriptionVisible(checked);
                  if (!checked) {
                    setEditedDescription("");
                    await onUpdateField({
                      field,
                      updated: { description: "" },
                    });
                  }
                }}
                onToggleMandatory={async (isMandatory) => {
                  await onUpdateField({
                    field,
                    updated: { mandatory: isMandatory },
                  });
                }}
                onToggleIncludeInSummary={async (checked) => {
                  await onUpdateField({
                    field,
                    updated: { includeInSummary: checked },
                  });
                }}
                onDelete={() => onDeleteField(field)}
                isViewingBaseLanguage={isViewingBaseLanguage}
                shouldShowWebEnquiryWarning={
                  !!hasSubmissionLinks &&
                  isWebEnquiryClientNameField(field, matterTypes)
                }
              />
            </div>
            {!isShrinking && (
              <FieldBox
                field={field}
                onUpdateField={onUpdateField}
                viewingLanguage={viewingLanguage}
                hasSubmissionLinks={hasSubmissionLinks}
              />
            )}
            <div
              className="flex items-center mt-[0.8rem] justify-between gap-4 mx-[0.4rem]"
              data-testid="field-card-footer"
            >
              <Tooltip
                placement="top-start"
                offsetBy={10}
                text={
                  isViewingBaseLanguage
                    ? undefined
                    : "To keep all languages in sync, any changes to the components must be made in the English version of the form."
                }
              >
                <FieldTypeIndicator
                  fieldType={type}
                  matterField={matterField}
                  layout={layout}
                  matterLayouts={matterLayouts}
                  matterTypes={matterTypes}
                  loadMatterFields={loadMatterFields}
                  groupMatterField={groupMatterField}
                  groupLayout={groupLayout}
                  onChangeFieldType={onChangeFieldType}
                  excludingFieldTemplates={excludingFieldTemplates}
                  isDisabled={!isViewingBaseLanguage}
                  allowChangeFieldType={allowChangeFieldType}
                />
              </Tooltip>
              {type !== "info" && (
                <ConditionsButton
                  numberOfConditions={conditionActions.getNumberOfConditions(
                    field.uri,
                  )}
                  onClick={() => conditionActions.setEditingCondition(field)}
                  size="small"
                  disabled={!isViewingBaseLanguage}
                />
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
